import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import Sidebar from './Sidebar';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import config from '../config';
import { format } from 'date-fns';
import SlotsTable from './SlotsTable';

const Slots = () => {
  const [slots, setSlots] = useState([]);
  const [date, setDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    loadSlots(date);
  }, [date]);

  const loadSlots = async (date) => {
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      const accessToken = localStorage.getItem('accessToken');
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await axios.get(`${config.apiBaseUrl}/api/slots`, {
        params: { date: formattedDate },
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'X-Timezone': timezone
        }
      });

      const convertedSlots = response.data.map(slot => ({
        ...slot,
        date: new Date(slot.date),
        time: format(new Date(slot.time), 'hh:mm a')
      }));

      setSlots(convertedSlots);
    } catch (error) {
      console.error('Error loading slots:', error);
    }
  };

  const handleToggleStatus = async (slotId, status) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.put(`${config.apiBaseUrl}/api/slots/${slotId}`, { status }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      loadSlots(date);
    } catch (error) {
      console.error('Error updating slot status', error);
    }
  };

  const handleAddSlot = () => {
    setIsModalOpen(true);
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${config.apiBaseUrl}/api/slots/${slotId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      loadSlots(date);
    } catch (error) {
      console.error('Error deleting slot', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const goToPreviousDay = () => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() - 1);
    setDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + 1);
    setDate(newDate);
  };

  return (
    <div className="flex min-h-screen bg-gray-800 text-white">
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden bg-gray-900 p-4 flex justify-between items-center">
          <button 
            onClick={toggleSidebar}
            className="text-white hover:text-gray-300"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">Slots</h1>
          <button 
            onClick={() => setShowControls(!showControls)}
            className="text-white hover:text-gray-300"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m0 6h9.75m-9.75 0a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 12H7.5m0 6h9.75m-9.75 0a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 18H7.5" />
            </svg>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6">
          {/* Desktop Header */}
          <h1 className="hidden lg:block text-2xl font-bold mb-4">Available Slots</h1>
          
          {/* Controls Section */}
          <div className={`mb-6 ${showControls ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-gray-700 lg:bg-transparent p-4 lg:p-0 rounded-lg lg:rounded-none">
              {/* Mobile Controls Header */}
              <div className="flex justify-between items-center mb-4 lg:hidden">
                <h2 className="text-lg font-semibold">Date Controls</h2>
                <button 
                  onClick={() => setShowControls(false)}
                  className="text-gray-300 hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center gap-4 lg:gap-2">
                {/* Date Picker */}
                <div className="flex flex-col sm:col-span-2 lg:col-span-1">
                  <label className="mb-2 text-sm font-medium lg:hidden">Select Date:</label>
                  <div className="text-black">
                    <DatePicker
                      selected={date}
                      onChange={(date) => setDate(date)}
                      dateFormat="yyyy-MM-dd"
                      className="rounded p-2 border border-gray-300 w-full lg:w-auto"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 sm:col-span-2 lg:flex lg:gap-2">
                  <button 
                    onClick={() => loadSlots(date)} 
                    className="bg-blue-500 hover:bg-blue-700 text-white px-3 lg:px-4 py-2 rounded text-sm transition-colors"
                  >
                    Search
                  </button>
                  <button 
                    onClick={goToPreviousDay} 
                    className="bg-gray-500 hover:bg-gray-700 text-white px-3 lg:px-4 py-2 rounded text-sm transition-colors"
                  >
                    Previous
                  </button>
                  <button 
                    onClick={goToNextDay} 
                    className="bg-gray-500 hover:bg-gray-700 text-white px-3 lg:px-4 py-2 rounded text-sm transition-colors"
                  >
                    Next
                  </button>
                  <button 
                    onClick={handleAddSlot} 
                    className="bg-green-500 hover:bg-green-700 text-white px-3 lg:px-4 py-2 rounded text-sm transition-colors sm:col-span-2 lg:col-span-1 lg:ml-auto"
                  >
                    Add Slot
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Slots Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
            {slots.map(slot => (
              <div 
                key={slot.sid} 
                className={`p-4 rounded-md flex flex-col items-center justify-center min-h-[200px] ${
                  slot.status ? 'bg-green-600' : 'bg-red-500'
                }`}
              >
                <h2 className="text-lg lg:text-2xl font-bold mb-2 text-center">
                  {format(new Date(slot.date), 'dd MMM yyyy')}
                </h2>
                <p className="text-base lg:text-lg mb-2 text-center">Time: {slot.time}</p>
                {slot.status ? (
                  <p className="text-base lg:text-lg mb-2 text-center">Status: Slot Available</p>
                ) : (
                  <p className="text-base lg:text-lg mb-2 text-center">Status: Slot Closed</p>
                )}
                {slot.appointment && (
                  <div className="text-sm lg:text-base mb-2 text-center">
                    <p><strong>Customer:</strong> {slot.appointment.name}</p>
                    <p><strong>Call:</strong> {slot.appointment.app_name}</p>
                    <p><strong>Meeting:</strong> {slot.appointment.link}</p>
                  </div>
                )}
                <div className="flex flex-col gap-2 w-full">
                  <button
                    onClick={() => handleToggleStatus(slot.sid, !slot.status)}
                    className={`px-3 py-2 rounded text-sm transition-colors ${
                      slot.status ? 'bg-red-500 hover:bg-red-700' : 'bg-green-500 hover:bg-green-700'
                    } text-white`}
                  >
                    {slot.status ? 'Close' : 'Open'}
                  </button>
                  <button 
                    onClick={() => handleDeleteSlot(slot.sid)} 
                    className="bg-gray-600 hover:bg-gray-800 text-white px-3 py-2 rounded text-sm transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {slots.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No slots found for {format(date, 'dd MMM yyyy')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <SlotsTable
            onClose={() => setIsModalOpen(false)}
            onSave={async (date, times) => {
              try {
                await axios.post(
                  `${config.apiBaseUrl}/api/slots/add`,
                  {
                    date,
                    times
                  },
                  {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                    }
                  }
                );
                
                setIsModalOpen(false);
                loadSlots(new Date(date));
              } catch (error) {
                console.error('Error adding slots:', error);
                alert('Error creating slots. Please try again.');
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Slots;